import React from 'react';
import '../../css/views/CaseDetail.scss';
import colors from '../../css/theme.module.scss';
import { FhirClientContext } from '../../FhirClientContext';
import { withTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { getOwnOrganization } from '../utils/getOwn';
import {
  getExternalServerUrl,
  getExternalServiceRequestId,
  isExternalReference,
} from '../utils/checkExternalReference';
import { Autocomplete } from '@mui/material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers } from '@fortawesome/free-solid-svg-icons';

class CaseHeader extends React.Component {
  static contextType = FhirClientContext;

  constructor(props) {
    super(props);
    this.state = {
      patient: {
        id: 0,
        name: '',
        height: '',
        birthdate: '',
        gender: '',
        weight: '',
      },
      case: {
        payer: '',
        partner: [],
        counterpart: '',
        callbacknumber: '',
      },
      isArchived: false,
      isCloseRequested: false,
      isCaseConference: false,
      ownOrganization: '',

      isConsultationGiver: false,
      dialogAddPartnerOpen: false,
      selectedPerformers: [],
      providedHcServices: {},
      organizations: [],
      hasFederations: false,
      federations: [],
    };
  }

  async loadData() {
    const client = this.context.client;
    const { t } = this.props;
    const currentCaseState = this.state.case;
    const currentPatientState = this.state.patient;
    await client
      .request(`ServiceRequest/${this.props.sr_id}`, {
        pageLimit: 0,
        flat: true,
      })
      .then(async sr => {
        await client
          .request(
            {
              url: `ServiceRequest/${getExternalServiceRequestId(sr, client) ?? this.props.sr_id}`,
              federatedServerUrl: getExternalServerUrl(sr, client),
            },
            {
              pageLimit: 0,
              flat: true,
              resolveReferences: ['performer', 'requester', 'contact'],
            }
          )
          .then(async data => {
            await client
              .request({ url: data.subject.reference, federatedServerUrl: getExternalServerUrl(sr, client) })
              .then(patientData => {
                const name = patientData.name;
                const patientName = this.buildPatientName(name);

                const externalId = getExternalServiceRequestId(sr, client);
                const externalUrl = getExternalServerUrl(sr, client);
                if (externalId) {
                  client
                    .request({ url: 'ServiceRequest/' + externalId, federatedServerUrl: externalUrl })
                    .then(srData => {
                      this.setState({ isCaseConference: srData.performer.length > 1 });
                    });
                } else {
                  this.setState({ isCaseConference: data.performer.length > 1 });
                }

                // case is archived
                if (client.getPath(data, 'status') == 'completed') {
                  this.setState({ isArchived: true });
                  this.props.setSrIsArchived(true);
                }

                // case should be closed
                if (client.getPath(data, 'status') == 'on-hold') {
                  this.setState({ isCloseRequested: true });
                  let holdBy = client.getPath(data, 'supportingInfo.0.reference');
                  if (holdBy.startsWith('PractitionerRole')) {
                    client
                      .request(`${holdBy}`, {
                        pageLimit: 0,
                        flat: true,
                      })
                      .then(practitionerRole => {
                        this.props.setSrIsOnHoldBy(practitionerRole.organization.reference);
                      });
                  } else {
                    this.props.setSrIsOnHoldBy(holdBy);
                  }
                }

                // am I requester or performer?
                let partner = '';

                // get requester organization
                let requesterOrg;
                if (client.getPath(data, 'requester.resourceType') == 'Organization') {
                  requesterOrg = 'Organization/' + client.getPath(data, 'requester.id');
                } else {
                  requesterOrg = client.getPath(data, 'requester.organization.reference');
                }

                let performers = data.performer.map(o => o.name);
                if (requesterOrg == this.state.ownOrganization) {
                  // I am requester
                  currentCaseState.partner = performers;
                  this.setState({
                    case: {
                      ...this.state.case,
                      partner: performers,
                    },
                    isConsultationGiver: true,
                  });

                  this.props.setSrRecipient(client.getPath(data, 'performer.0.id'));
                  this.props.setSrConsultationGiver(true);
                } else {
                  // I am performer
                  partner = client.getPath(data, 'requester.name');
                  client
                    .request(
                      { url: `${requesterOrg}`, federatedServerUrl: externalUrl },
                      {
                        pageLimit: 0,
                        flat: true,
                      }
                    )
                    .then(partner => {
                      currentCaseState.partner = partner.name;

                      this.setState({
                        case: {
                          ...this.state.case,
                          partner: partner.name,
                        },
                      });

                      this.props.setSrRecipient(partner.id);
                    });
                }

                client
                  .request(
                    {
                      url: `Observation?subject=Patient/${patientData.id}`,
                      federatedServerUrl: externalUrl,
                    },
                    {
                      pageLimit: 0,
                      flat: true,
                    }
                  )
                  .then(data => {
                    data.forEach(o => {
                      // get weight
                      if (client.getPath(o, 'code.coding.0.code') == '29463-7') {
                        currentPatientState.weight = client.getPath(o, 'valueQuantity.value') + ' ' + client.getPath(o, 'valueQuantity.unit');

                        this.setState({
                          patient: {
                            ...this.state.patient,
                            weight:
                              client.getPath(o, 'valueQuantity.value') + ' ' + client.getPath(o, 'valueQuantity.unit'),
                          },
                        });

                      }

                      // get height
                      if (client.getPath(o, 'code.coding.0.code') == '8302-2') {
                        currentPatientState.height = client.getPath(o, 'valueQuantity.value') + ' ' + client.getPath(o, 'valueQuantity.unit');

                        this.setState({
                          patient: {
                            ...this.state.patient,
                            height:
                              client.getPath(o, 'valueQuantity.value') + ' ' + client.getPath(o, 'valueQuantity.unit'),
                          },
                        });

                      }
                    });
                  });

                client
                  .request(
                    {
                      url: `Coverage?policy-holder=Patient/${patientData.id}`,
                      federatedServerUrl: externalUrl,
                    },
                    {
                      pageLimit: 0,
                      flat: true,
                    }
                  )
                  .then(data => {
                    currentCaseState.payer = client.getPath(data, '0.class.0.name');

                    this.setState({
                      case: {
                        ...this.state.case,
                        payer: client.getPath(data, '0.class.0.name'),
                      },
                    });

                  });

                currentCaseState.counterpart = patientData.contact[0].name.family;
                currentCaseState.callbacknumber = patientData.contact[0].telecom ? patientData.contact[0].telecom[0].value : '';
                currentPatientState.id = patientData.id;
                currentPatientState.name = patientName;
                currentPatientState.gender = t(patientData.gender);
                currentPatientState.birthdate = format(parseISO(patientData.birthDate), 'P', { locale: de });

                this.setState({
                  patient: {
                    ...this.state.patient,
                    id: patientData.id,
                    name: patientName,
                    gender: t(patientData.gender),
                    birthdate: format(parseISO(patientData.birthDate), 'P', { locale: de }),
                  },
                  case: {
                    ...this.state.case,
                    counterpart: patientData.contact[0].name.family ? patientData.contact[0].name.family : '',
                    callbacknumber: patientData.contact[0].telecom ? patientData.contact[0].telecom[0].value : '',
                  },
                });

                this.props.setSrRequester(client.getPath(data, 'requester'));
                this.props.setSrSubject(patientData.id);
              });
          });
      });
      this.setState({case: currentCaseState, patient: currentPatientState});
  }

  loadHcsData() {
    const client = this.context.client;

    // we need to download all healthcare services of every federated network as well as our own
    // we then save this into a new object to be sure to have the matching server address as well
    // key value pair with healthcare type as key, value an object of server and healthcareservice

    client
      .request(`/List?code=federation`, {
        pageLimit: 0,
        flat: true,
      })
      .then(data => {
        this.setState({ organizations: [] });
        if (data[0] && data[0].entry) {
          this.setState({ hasFederations: data[0].entry.length > 0 });
          data[0].entry.forEach(federatedInstance => {
            this.setState({
              federations: [
                ...this.state.federations,
                { title: federatedInstance.item.display, url: federatedInstance.item.identifier.value },
              ],
            });
          });
        }
        client
          .request('/HealthcareService', { pageLimit: 0, flat: true, resolveReferences: ['providedBy'] })
          .then(hcsData => {
            if (hcsData) {
              hcsData.filter(data => data.identifier[0].system === 'clinic.amp.specialty');
              this.mapDataToState(hcsData, null);
            }
            if (this.state.hasFederations) {
              this.state.federations
                .map(fed => fed.url)
                .map(fedUrl => {
                  client
                    .request(
                      { url: '/HealthcareService', federatedServerUrl: fedUrl },
                      { pageLimit: 0, flat: true, resolveReferences: ['providedBy'] }
                    )
                    .then(hcData => {
                      if (hcData) {
                        hcData.filter(data => data.identifier[0].system === 'clinic.amp.specialty');
                        this.mapDataToState(hcData, fedUrl);
                      }
                    });
                });
            }
          });
      });
  }

  mapDataToState = (healthcareServiceData, serverUrl) => {
    const hcKeys = Object.keys(this.state.providedHcServices);
    let dataToSave = this.state.providedHcServices;

    // structure of the state variable providedHcServices: { key: healthcareservice identifier string, value: [healthcareservice object] }
    healthcareServiceData.map(data => {
      data.fedUrl = serverUrl;
      if (hcKeys.includes(data.identifier[0].value)) {
        dataToSave[data.identifier[0].value].push(data);
      } else {
        dataToSave[data.identifier[0].value] = [data];
        hcKeys.push(data.identifier[0].value);
      }
    });

    this.setState({ providedHcServices: dataToSave });
  };

  handleChange = (event, val, service) => {
    this.setState({ selectedPerformers: val });
  };

  buildPatientName(name) {
    const { t } = this.props;
    if (!name) return t('No Name');

    const entry = name.find(nameRecord => nameRecord.use === 'official') || name[0];
    if (!entry) return t('No Name');

    return entry.given.join(' ') + ' ' + entry.family;
  }

  onAddPartnerButtonClick = () => {
    this.setState({ dialogAddPartnerOpen: true });
  };

  onAddPartnerDialogClose = () => {
    this.setState({ dialogAddPartnerOpen: false });
  };

  onAddPartner = async () => {
    const client = this.context.client;

    console.log("adding partner ");
    console.log(this.state.selectedPerformers);

    // update ServiceRequest
    const performerList = this.state.selectedPerformers.map(performer => {
      return { reference: 'Organization/' + performer.providedBy.id };
    });

    const serviceRequest = await client.request(`/ServiceRequest/${this.props.sr_id}`, {
      pageLimit: 0,
      flat: true,
    });

    serviceRequest.performer.push(...performerList);
    console.log(serviceRequest);

    // update ServiceRequest
    await client.update(serviceRequest);

    // reset dialog
    this.setState({ selectedPerformers: [], dialogAddPartnerOpen: false });
  };

  componentWillUnmount() {}

  async componentDidMount() {
    // check for own organization
    const client = this.context.client;
    this.setState({ ownOrganization: await getOwnOrganization(client) });

    await this.loadData();
    await this.loadHcsData();
  }

  render() {
    const { t } = this.props;
    const archivedHeader =
      this.state.isArchived || this.props.srIsArchived ? (
        <div className="amp archivedHeader">
          {t("This consultation is archived and can't be edited.")}<br/>
        </div>
      ) : null;
    const closeRequestedHeader =
      this.state.isCloseRequested || this.props.srIsOnHoldBy ? (
        <div className="amp archivedHeader">
          {t('This consultation is readonly because there is an open request for closing this consultation.')}
        </div>
      ) : null;

    const leftHeaderBlock = (
      <div className="blockLeft">
        <div className="amp casePartner">
          <FontAwesomeIcon
            style={{
              display: this.state.isCaseConference ? 'inline' : 'none',
              color: colors.ampDarkestGrey,
            }}
            icon={faUsers}
          />
          <span
            title={
              typeof this.state.case.partner == 'string' ? this.state.case.partner : this.state.case.partner.join(', ')
            }
            style={{ marginLeft: '.2em' }}
          >
            {typeof this.state.case.partner == 'string'
              ? this.state.case.partner
              : this.state.case.partner.length > 1
              ? t('Case conference with participants', { count: this.state.case.partner.length })
              : this.state.case.partner[0]}{' '}
          </span>
          <Button
            variant="contained"
            color="primary"
            onClick={this.onAddPartnerButtonClick}
            disabled={!this.state.isConsultationGiver}
          >
            {t('Add partner')}
          </Button>
        </div>
        <div className="counterpart">{t('Counterpart')}</div>
        <div className="horizontalSeparator"></div>
        <div className="insurance">{t('Insurance')}</div>
        <div className="amp casePayer">{this.state.case.payer}</div>
      </div>
    );

    const middleHeaderBlock = (
      <div className="parent">
        <div className="amp patientNameInfo" style={{ fontSize: '28px' }}>
          {this.state.patient.name}
        </div>
        <div className="amp upperLeftInfo tbody" style={{ justifySelf: 'flex-end' }}>
          {this.state.patient.birthdate}
        </div>
        <div className="upperHorizontalDivider">
          <div className="horizontalSeparator"></div>
        </div>
        <div className="lowerHorizontalDivider">
          <div className="horizontalSeparator"></div>
        </div>
        <div className="amp upperRightInfo tbody" style={{ justifySelf: 'flex-start' }}>
          {this.state.patient.gender}
        </div>
        <div className="amp lowerLeftInfo tbody" style={{ justifySelf: 'flex-end' }}>
          {this.state.patient.height}
        </div>
        <div className="amp lowerRightInfo tbody" style={{ justifySelf: 'flex-start' }}>
          {this.state.patient.weight}
        </div>
        <div className="verticalDivider">
          <div className="verticalSeparator"></div>
        </div>
      </div>
    );

    const rightHeaderBlock = (
      <div className="blockRight">
        <div className="amp caseCounterpart">{this.state.case.counterpart}</div>
        <div className="contactPerson">{t('Contact person')}</div>
        <div className="horizontalSeparator"></div>
        <div className="cNumber">{t('Callback Number')}</div>
        <div className="amp callbackNumber">{this.state.case.callbacknumber}</div>
      </div>
    );

    const dialog = (
      <Dialog open={this.state.dialogAddPartnerOpen} onClose={this.onAddPartnerDialogClose} maxWidth="sm" fullWidth>
          <div>
            <DialogTitle>{t('Add partner to consultation')}</DialogTitle>
            <DialogContent dividers>
            <div className="healthCareServices">
            {Object.keys(this.state.providedHcServices).length > 0 ? (
              Object.keys(this.state.providedHcServices).map(service => {
                return (
                  <Autocomplete
                    key={service}
                    value={this.state.selectedPerformers.filter(o => o.identifier[0].value == service) || []}
                    sx={{ m: 1, width: 500 }}
                    multiple
                    options={
                      this.state.selectedPerformers[0] === undefined ||
                      this.state.selectedPerformers[0].identifier[0].value !== service
                        ? this.state.providedHcServices[service].map(o => o)
                        : this.state.providedHcServices[service]
                            .filter(service => service.fedUrl === this.state.selectedPerformers[0].fedUrl)
                            .map(o => o)
                    }
                    getOptionLabel={option => option.name}
                    disableCloseOnSelect
                    renderInput={params => (
                      <TextField
                        {...params}
                        variant="outlined"
                        key={this.state.providedHcServices[service][0]?.id}
                        label={this.state.providedHcServices[service][0]?.type[0].coding[0].display}
                      />
                    )}
                    onChange={(event, val) => this.handleChange(event, val, service)}
                  />
                );
              })
            ) : (
              <div />
            )}
          </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={this.onAddPartnerDialogClose} color="primary">
                {t('Cancel')}
              </Button>
              <Button onClick={this.onAddPartner} color="primary">
                {t('Invite')}
              </Button>
            </DialogActions>
          </div>
        </Dialog>
    );

    return (
      <>
        <div className="amp caseHeader">
          {leftHeaderBlock}
          {middleHeaderBlock}
          {rightHeaderBlock}
        </div>
        {archivedHeader}
        {closeRequestedHeader}
        <a href="https://www.periop.de/befragung3" target="_blank" rel="noopener" style={{ color: "#66b0f0", fontSize: "20px" }}>{t("Please complete the survey: Click here")}</a>
        {dialog}
      </>
    );
  }
}

export default withTranslation()(CaseHeader);
